// PlatformUsageReservoirPanel — May 29, 2026 (Phase B rebuild).
//
// The Platform LLM bucket. Mental model:
//   - Reservoir = live Stripe platform-account balance.
//   - Inflows = creator voucher top-ups (tagged via stripe metadata).
//   - Outflows = ACH pulls by Gemini / Anthropic / OpenAI consoles
//     (tagged via descriptor pattern matching).
//   - Margin = inflows − outflows (the 3× spread you earn).
//   - Bank routing = 3 buckets (Reservoir / Site Profits / Founder Personal)
//     with editable last-4 fields. Sandbox uses fake numbers; flip to
//     live by swapping the last-4s — zero code change.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowsClockwise, ArrowDown, ArrowUp, Bank, Sparkle, Spinner, Warning,
  Pencil, Check, X as XIcon, ArrowRight, Bandaids,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmt$ = (cents) => {
  const n = (cents || 0) / 100;
  const abs = Math.abs(n);
  const formatted = abs.toLocaleString(undefined, {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });
  return `${n < 0 ? "−" : ""}$${formatted}`;
};

const PROVIDER_COLORS = {
  gemini: "#4A5D4E",
  anthropic: "#C16E5A",
  openai: "#3A4A3E",
  site_keep: "#1C1B1A",
  other: "#6B6A68",
};

const PROVIDER_LABELS = {
  gemini: "Gemini",
  anthropic: "Anthropic",
  openai: "OpenAI",
  other: "Unattributed",
};

export default function PlatformUsageReservoirPanel({ exposure = null, liabilityCents = 0 }) {
  const [snap, setSnap] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [covering, setCovering] = useState(false);
  const [sweepOpen, setSweepOpen] = useState(false);

  const reload = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/reservoir/snapshot"),
        api.get("/admin/reservoir/change-log?limit=50"),
      ]);
      setSnap(s.data);
      setLog(l.data?.rows || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load reservoir.");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/reservoir/sync");
      const n = r.data?.new_rows || 0;
      toast.success(n > 0 ? `Synced — ${n} new event${n === 1 ? "" : "s"} from Stripe.` : "Synced — no new events.");
      await reload();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed.");
    } finally { setSyncing(false); }
  };

  const handleCoverFees = async () => {
    setCovering(true);
    try {
      const r = await api.post("/admin/reservoir/cover-residual-fees");
      if (r.data?.ok) {
        toast.success(r.data.message || "Residual fees covered.");
        // Wait a beat for Stripe to settle the new pending entry, then reload.
        setTimeout(() => reload(), 2500);
      } else {
        toast.info(r.data?.message || "Nothing to cover.");
        await reload();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cover failed.");
    } finally { setCovering(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-[#6B6A68]" data-testid="reservoir-loading">
        <Spinner size={14} className="animate-spin" /> Loading reservoir…
      </div>
    );
  }
  if (!snap) return null;

  const bal = snap.live_balance || {};        // voucher-only ledger (headline)
  const stripe = snap.stripe_balance || {};   // real Stripe acct (sweep/cover only)
  const pm = snap.profit_math || {};
  const buckets = snap.bank_routing || [];

  return (
    <section className="mt-10 pt-8 border-t hairline space-y-6" data-testid="reservoir-panel">
      {/* Header */}
      <div className="flex items-baseline gap-3 mb-1 flex-wrap">
        <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Platform Usage Reservoir</h2>
        <span className="text-[10px] uppercase tracking-[0.22em] text-[#6B6A68]">
          live · voucher ledger
        </span>
        {/* Mar 2026 — Conditional "Cover residual fees" pill. Only shows
            when (a) Stripe balance is negative AND (b) we're in sandbox.
            Disappears the moment balance ≥ 0. Keeps the header clean
            day-to-day. */}
        {(stripe.total_cents || 0) < 0 && (
          <button
            onClick={handleCoverFees}
            disabled={covering}
            data-testid="reservoir-cover-fees-btn"
            title={`Stripe kept ${fmt$(Math.abs(stripe.total_cents))} in processing fees from your refunds. Click to create a tiny sandbox top-up that zeroes the balance.`}
            className="ml-auto sm:ml-3 text-[10px] px-2 py-1 rounded-full bg-[#FDF4E8] border border-[#C16E5A] text-[#7A3A2A] hover:bg-[#F7E4C8] flex items-center gap-1 disabled:opacity-50"
          >
            <Bandaids size={11} weight="duotone" />
            {covering ? "Covering…" : `Cover residual fees (${fmt$(Math.abs(stripe.total_cents))})`}
          </button>
        )}
        <button
          onClick={handleSync}
          disabled={syncing}
          data-testid="reservoir-sync-btn"
          className={`${(stripe.total_cents || 0) < 0 ? "" : "ml-auto"} text-xs px-3 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1.5 disabled:opacity-50`}
        >
          <ArrowsClockwise size={12} weight="bold" className={syncing ? "animate-spin" : ""} />
          {syncing ? "Syncing…" : "Sync from Stripe"}
        </button>
      </div>

      {/* Live balance + profit math */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="reservoir-kpis">
        <KpiCard
          label="Live balance · voucher reservoir"
          value={fmt$(bal.total_cents)}
          sub="voucher top-ups − your real LLM cost"
          accent="#1C1B1A"
          big
          testid="reservoir-live-balance"
          tooltip="Voucher money only. Affiliate-gate fees and subscription revenue are NOT counted here — this tracks the voucher system in isolation."
        />
        <KpiCard
          label="Inflows · all time"
          value={fmt$(pm.inflow_total_cents)}
          sub={`${pm.inflow_count || 0} top-ups detected`}
          accent="#2F6B3A"
          icon={ArrowUp}
          testid="reservoir-inflows"
        />
        <KpiCard
          label="Outflows · all time"
          value={fmt$(pm.outflow_total_cents)}
          sub={`${pm.outflow_count || 0} platform-key calls · real provider cost`}
          accent="#7A3A2A"
          icon={ArrowDown}
          testid="reservoir-outflows"
        />
        <KpiCard
          label="Margin · all time"
          value={fmt$(pm.margin_cents)}
          sub="what creators paid − your real cost = profit"
          accent={(pm.margin_cents || 0) >= 0 ? "#2F6B3A" : "#7A3A2A"}
          icon={Sparkle}
          big
          testid="reservoir-margin"
        />
      </div>

      {/* Voucher exposure — Jun 2026 (Randy). Sits directly below the reservoir
          KPI cards, above the sweep + change-log rows. */}
      {exposure && <VoucherExposureBlock exposure={exposure} liabilityCents={liabilityCents} />}

      {/* Sweep + last sync */}
      <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B6A68]">
        <span>
          Last sync · {snap.last_sync_at ? new Date(snap.last_sync_at).toLocaleString() : "never"}
        </span>
        <button
          onClick={() => setSweepOpen(true)}
          disabled={(stripe.available_cents || 0) <= 0}
          data-testid="reservoir-sweep-btn"
          className="ml-auto text-xs px-3 py-1.5 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5"
          title={(stripe.available_cents || 0) <= 0 ? "No available Stripe balance to sweep" : "Move profits to your Site Profits bank"}
        >
          <Bank size={12} weight="duotone" />
          Sweep to Site Keep
        </button>
      </div>

      {/* Change log */}
      <div data-testid="reservoir-change-log">
        <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2">
          Change log · {log.length} event{log.length === 1 ? "" : "s"}
        </div>
        {log.length === 0 ? (
          <div className="bg-white border hairline rounded-lg p-6 text-center text-xs text-[#6B6A68] italic">
            No events yet. Click "Sync from Stripe" to pull recent inflows + outflows.
          </div>
        ) : (
          <div className="bg-white border hairline rounded-lg divide-y hairline max-h-[400px] overflow-y-auto">
            {log.map((r) => <LogRow key={r.id} r={r} />)}
          </div>
        )}
      </div>

      {/* Bank routing */}
      <BankRoutingCard buckets={buckets} onChange={reload} />

      {/* Sweep modal */}
      {sweepOpen && (
        <SweepModal
          available={stripe.available_cents || 0}
          siteProfits={buckets.find((b) => b.key === "site_profits")}
          onClose={() => setSweepOpen(false)}
          onDone={() => { setSweepOpen(false); reload(); }}
        />
      )}
    </section>
  );
}

function KpiCard({ icon: Icon, label, value, sub, accent, big, testid, tooltip }) {
  return (
    <div className="bg-white border hairline rounded-lg p-4" data-testid={testid} title={tooltip}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon && <Icon size={14} weight="duotone" style={{ color: accent }} />}
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">{label}</div>
      </div>
      <div className={`font-serif-display ${big ? "text-3xl" : "text-2xl"} tabular-nums`} style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="text-[10px] text-[#6B6A68] mt-1 leading-relaxed">{sub}</div>}
    </div>
  );
}

function LogRow({ r }) {
  const isOut = r.direction === "outflow";
  const sign = isOut ? "−" : "+";
  const color = isOut ? "#7A3A2A" : "#2F6B3A";
  const tag = r.provider || r.type;
  const tagColor = PROVIDER_COLORS[r.provider] || PROVIDER_COLORS.other;
  return (
    <div className="px-4 py-2.5 flex items-center gap-3 text-xs" data-testid={`log-row-${r.id}`}>
      <span
        className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded text-white whitespace-nowrap"
        style={{ background: tagColor }}
      >
        {tag}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[#1C1B1A] truncate">
          {r.description || (isOut ? "outflow" : "inflow")}
        </div>
        <div className="text-[10px] text-[#6B6A68]">
          {r.stripe_created ? new Date(r.stripe_created * 1000).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
          }) : "—"}
        </div>
      </div>
      <div className="text-xs font-mono tabular-nums shrink-0" style={{ color }}>
        {sign}{fmt$(Math.abs(r.amount_cents || 0))}
      </div>
      <div className="text-[10px] text-[#6B6A68] font-mono shrink-0 hidden md:block">
        bal {fmt$(r.balance_after_cents)}
      </div>
    </div>
  );
}

function BankRoutingCard({ buckets, onChange }) {
  return (
    <div data-testid="reservoir-bank-routing">
      <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2">
        Bank routing · 3-bucket split
      </div>
      <div className="bg-white border hairline rounded-lg p-4 space-y-3">
        <div className="text-[11px] text-[#6B6A68] leading-relaxed">
          The 3 buckets that wire the financial flow. Sandbox uses Stripe's fake test bank numbers — when you flip to live, swap the last-4s for your real Relay sub-account numbers. Zero code change.
        </div>
        {buckets.map((b) => <BucketRow key={b.key} bucket={b} onChange={onChange} />)}
      </div>
    </div>
  );
}

function BucketRow({ bucket, onChange }) {
  const [editing, setEditing] = useState(false);
  const [nickname, setNickname] = useState(bucket.nickname || "");
  const [acctLast4, setAcctLast4] = useState(bucket.account_last4 || "");
  const [routeLast4, setRouteLast4] = useState(bucket.routing_last4 || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/reservoir/bank-routing", {
        bucket: bucket.key,
        nickname: nickname || null,
        account_last4: acctLast4 || null,
        routing_last4: routeLast4 || null,
      });
      toast.success(`Saved ${bucket.label.split("(")[0].trim()}.`);
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.put("/admin/reservoir/bank-routing", { bucket: bucket.key, clear: true });
      toast.success("Cleared.");
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed.");
    } finally { setBusy(false); }
  };

  const configured = bucket.account_last4 || bucket.nickname;
  return (
    <div className="border hairline rounded-md p-3" data-testid={`bucket-${bucket.key}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-[#1C1B1A]">{bucket.label}</div>
          {!editing && (
            <div className="text-[11px] text-[#6B6A68] mt-1">
              {configured ? (
                <>
                  {bucket.nickname && <span className="text-[#1C1B1A]">{bucket.nickname}</span>}
                  {bucket.account_last4 && <span className="ml-2 font-mono">acct ····{bucket.account_last4}</span>}
                  {bucket.routing_last4 && <span className="ml-2 font-mono">route ····{bucket.routing_last4}</span>}
                </>
              ) : (
                <span className="italic">Not configured yet</span>
              )}
            </div>
          )}
        </div>
        {!editing ? (
          <button
            onClick={() => setEditing(true)}
            data-testid={`bucket-edit-${bucket.key}`}
            className="text-[11px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1"
          >
            <Pencil size={10} /> {configured ? "Edit" : "Configure"}
          </button>
        ) : null}
      </div>
      {editing && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2">
          <input
            type="text" placeholder="Nickname (e.g. Relay Reservoir)"
            value={nickname} onChange={(e) => setNickname(e.target.value)}
            data-testid={`bucket-nickname-${bucket.key}`}
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E]"
          />
          <input
            type="text" placeholder="Acct last 4 (e.g. 6789)" maxLength={4}
            value={acctLast4} onChange={(e) => setAcctLast4(e.target.value.replace(/\D/g, ""))}
            data-testid={`bucket-acct-${bucket.key}`}
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono tabular-nums"
          />
          <input
            type="text" placeholder="Routing last 4 (e.g. 9588)" maxLength={4}
            value={routeLast4} onChange={(e) => setRouteLast4(e.target.value.replace(/\D/g, ""))}
            data-testid={`bucket-route-${bucket.key}`}
            className="px-2 py-1.5 border hairline rounded text-xs bg-white focus:outline-none focus:border-[#4A5D4E] font-mono tabular-nums"
          />
          <div className="md:col-span-3 flex gap-2">
            <button
              onClick={save} disabled={busy}
              data-testid={`bucket-save-${bucket.key}`}
              className="text-[11px] px-3 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1"
            >
              <Check size={11} /> Save
            </button>
            <button
              onClick={() => setEditing(false)}
              data-testid={`bucket-cancel-${bucket.key}`}
              className="text-[11px] px-3 py-1.5 rounded border hairline hover:border-[#C16E5A] flex items-center gap-1"
            >
              <XIcon size={11} /> Cancel
            </button>
            {configured && (
              <button
                onClick={clear} disabled={busy}
                data-testid={`bucket-clear-${bucket.key}`}
                className="ml-auto text-[11px] px-3 py-1.5 rounded text-[#7A3A2A] border border-[#E5BBA8] hover:bg-[#FDF1ED] disabled:opacity-50"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SweepModal({ available, siteProfits, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const cents = Math.round((parseFloat(amount) || 0) * 100);
  const valid = cents > 0 && cents <= available;
  const configured = !!siteProfits?.account_last4;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/reservoir/sweep", {
        amount_dollars: parseFloat(amount),
        note: note || null,
      });
      toast.success(`Sweep queued: ${fmt$(r.data.plan.amount_cents)} → ${r.data.plan.destination_label}`);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sweep failed.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="sweep-modal">
      <div className="modal-safe bg-[#FDFCF8] border hairline rounded-lg p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="font-serif-display text-xl text-[#1C1B1A] mb-1">Sweep to Site Keep</div>
        <div className="text-xs text-[#6B6A68] mb-4">
          Move profits from your Stripe platform balance to your Site Profits bank.
        </div>
        {!configured ? (
          <div className="bg-[#FDF1ED] border border-[#E5BBA8] rounded-md p-3 text-xs text-[#7A3A2A] mb-3 flex gap-2">
            <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
            <span>Configure Site Profits bank routing first (last 4 of account + routing required).</span>
          </div>
        ) : (
          <div className="bg-white border hairline rounded-md p-3 text-xs mb-3">
            <div className="flex items-center gap-2 text-[#6B6A68]">
              <span>Available to sweep</span>
              <span className="ml-auto font-mono text-[#1C1B1A]">{fmt$(available)}</span>
            </div>
            <div className="flex items-center gap-2 text-[#6B6A68] mt-1">
              <span>Destination</span>
              <span className="ml-auto text-[#1C1B1A] truncate">
                {siteProfits.nickname || "Site Profits"} <ArrowRight size={10} className="inline" /> ····{siteProfits.account_last4}
              </span>
            </div>
          </div>
        )}
        <div className="space-y-2">
          <div className="flex items-center gap-1.5">
            <span className="text-sm text-[#1C1B1A]">$</span>
            <input
              type="number" min="0.01" step="0.01" placeholder="amount"
              value={amount} onChange={(e) => setAmount(e.target.value)}
              data-testid="sweep-amount-input"
              className="flex-1 px-2 py-1.5 border hairline rounded text-sm bg-white focus:outline-none focus:border-[#4A5D4E] tabular-nums"
            />
          </div>
          <input
            type="text" placeholder="note (optional)"
            value={note} onChange={(e) => setNote(e.target.value)}
            data-testid="sweep-note-input"
            className="w-full px-2 py-1.5 border hairline rounded text-sm bg-white focus:outline-none focus:border-[#4A5D4E]"
          />
        </div>
        <div className="flex gap-2 mt-4">
          <button
            onClick={submit} disabled={busy || !valid || !configured}
            data-testid="sweep-submit-btn"
            className="flex-1 text-xs px-3 py-2 rounded-md bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-50"
          >
            {busy ? "Queuing…" : "Queue sweep"}
          </button>
          <button
            onClick={onClose}
            data-testid="sweep-cancel-btn"
            className="text-xs px-3 py-2 rounded-md border hairline hover:border-[#C16E5A]"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// Jun 2026 (Randy) — Voucher exposure. Answers two questions the Financials
// tab couldn't: how much unused voucher value do we owe creators, and what
// would it cost US to actually serve it?
//   • Outstanding = un-spent voucher cents summed across EVERY silo (unscoped).
//   • Est. provider liability = outstanding ÷ the stamped markup — the same
//     est_cost/markup_x math the reservoir applies to SPENT usage, run against
//     the unspent balance instead.
// `platform_key_voucher_cents` is the LEGACY combined field, kept in sync as
// the sum of the three per-provider buckets. It's the TOTAL, not a fourth
// provider — rendering it as a row would double-count the liability.
function VoucherExposureBlock({ exposure, liabilityCents }) {
  const rows = exposure?.by_provider || [];
  const mx = exposure?.markup_x || 3;
  const drift = exposure?.drift_cents || 0;
  return (
    <div data-testid="voucher-exposure">
      <div className="text-xs uppercase tracking-[0.22em] text-[#6B6A68] mb-2">
        Voucher exposure · unused credits across every silo
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <KpiCard
          label="Outstanding Voucher Liability"
          value={fmt$(liabilityCents)}
          sub="credits creators have paid for and not yet burned"
          accent="#7A3A2A"
          icon={ArrowDown}
          big
          testid="voucher-liability-total"
          tooltip="Cross-silo sum of the legacy platform_key_voucher_cents field — the combined total across all three provider buckets. Not a fourth provider."
        />
        <KpiCard
          label="Est. Provider Liability"
          value={fmt$(exposure?.est_provider_liability_cents)}
          sub={`what serving those credits would cost YOU at ${mx}× markup`}
          accent="#8A5A00"
          icon={Warning}
          big
          testid="voucher-provider-liability-total"
          tooltip={`Outstanding ÷ ${mx}× — the same est_cost/markup_x logic the reservoir uses on spent usage, applied to the unspent balance instead.`}
        />
      </div>
      <div className="mt-3 bg-white border hairline rounded-lg overflow-hidden" data-testid="voucher-exposure-table">
        <div className="px-4 py-2 flex items-center gap-3 text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] border-b hairline bg-[#F7F6F1]">
          <span className="flex-1">Provider bucket</span>
          <span className="w-24 text-right">Outstanding</span>
          <span className="w-28 text-right">Est. provider cost</span>
        </div>
        {rows.map((r) => (
          <div
            key={r.provider}
            className="px-4 py-2.5 flex items-center gap-3 text-xs border-b hairline"
            data-testid={`voucher-exposure-row-${r.provider}`}
          >
            <span
              className="w-2 h-2 rounded-full shrink-0"
              style={{ background: PROVIDER_COLORS[r.provider] || PROVIDER_COLORS.other }}
            />
            <span className="flex-1 text-[#1C1B1A]">{PROVIDER_LABELS[r.provider] || r.provider}</span>
            <span className="w-24 text-right font-mono tabular-nums text-[#1C1B1A]">{fmt$(r.outstanding_cents)}</span>
            <span className="w-28 text-right font-mono tabular-nums text-[#7A5200]">{fmt$(r.est_provider_cost_cents)}</span>
          </div>
        ))}
        {(exposure?.frozen_total_cents || 0) > 0 && (
          <div
            className="px-4 py-2.5 flex items-center gap-3 text-xs border-b hairline bg-[#FDF6EE]"
            data-testid="voucher-exposure-frozen"
          >
            <span className="w-2 h-2 rounded-full shrink-0 bg-[#8A5A00]" />
            <span className="flex-1 text-[#1C1B1A]">
              Frozen by open disputes
              <span className="text-[#6B6A68] ml-1.5">
                · {exposure.frozen_lot_count} lot{exposure.frozen_lot_count === 1 ? "" : "s"} · held outside spendable
              </span>
            </span>
            <span className="w-24 text-right font-mono tabular-nums text-[#8A5A00]">{fmt$(exposure.frozen_total_cents)}</span>
            <span className="w-28 text-right font-mono tabular-nums text-[#8A5A00]">{fmt$(exposure.est_frozen_provider_cost_cents)}</span>
          </div>
        )}
        <div
          className="px-4 py-2.5 flex items-center gap-3 text-xs bg-[#F7F6F1]"
          data-testid="voucher-exposure-row-platform_key"
        >
          <span className="w-2 h-2 rounded-full shrink-0" style={{ background: PROVIDER_COLORS.site_keep }} />
          <span className="flex-1 text-[#1C1B1A]">
            Platform Key
            <span className="text-[#6B6A68] ml-1.5">· legacy combined total, not a provider</span>
          </span>
          <span className="w-24 text-right font-mono tabular-nums text-[#1C1B1A]">{fmt$(liabilityCents)}</span>
          <span className="w-28 text-right font-mono tabular-nums text-[#7A5200]">
            {fmt$(exposure?.est_provider_liability_cents)}
          </span>
        </div>
      </div>
      {drift !== 0 ? (
        <div
          className="mt-2 bg-[#FDF1ED] border border-[#E5BBA8] rounded-md p-3 text-[11px] text-[#7A3A2A] flex gap-2"
          data-testid="voucher-exposure-drift"
        >
          <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span>
            <strong>Bucket drift · {fmt$(Math.abs(drift))}.</strong> The three provider buckets sum to{" "}
            {fmt$(exposure?.bucket_sum_cents)} but the legacy Platform Key total reads {fmt$(liabilityCents)}.
            They&apos;re supposed to match — a debit likely landed on one side only.
          </span>
        </div>
      ) : (
        <div className="mt-2 text-[10px] text-[#6B6A68] italic" data-testid="voucher-exposure-reconciled">
          Buckets reconcile · Gemini + Anthropic + OpenAI = Platform Key legacy total
          {(exposure?.lot_count || 0) > 0 && (
            <> · {exposure.lot_count} credit lot{exposure.lot_count === 1 ? "" : "s"} tracked, {fmt$(exposure.lot_active_total_cents)} active</>
          )}
          {(exposure?.reversed_total_cents || 0) > 0 && (
            <> · {fmt$(exposure.reversed_total_cents)} reversed by refunds/disputes</>
          )}
        </div>
      )}
      {(exposure?.review_open_count || 0) > 0 && (
        <div
          className="mt-2 bg-[#FBE9E1] border border-[#8A5A00] rounded-md p-3 text-[11px] text-[#7A5200] flex gap-2"
          data-testid="voucher-review-queue-banner"
        >
          <Warning size={14} weight="duotone" className="shrink-0 mt-0.5" />
          <span>
            <strong>
              {exposure.review_open_count} financial review item
              {exposure.review_open_count === 1 ? "" : "s"} · {fmt$(exposure.review_open_cents)}.
            </strong>{" "}
            Refunds or lost disputes that landed on credit already spent or withdrawn — these need a
            decision rather than an automatic reversal.
          </span>
        </div>
      )}
    </div>
  );
}

